<script lang="tsx">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'CertFile',
})
</script>
<template>
  <div class="page">
    <ListPage ref="listRef" border :fetch="fetch" :columns="columns">
      <template #search>
        <el-button type="primary" @click="handleDownloadCertDigital" :loading="btnLoading">一键下载</el-button>
      </template>
    </ListPage>
  </div>
</template>
<script setup lang="tsx" name="CertFile">
import { ListPage } from 'emui-plus';
import { computed, inject, onMounted, ref, watch } from 'vue';
import { cffeApi } from '@/api';
import { useAsyncLoading } from '@/hooks';
import { download } from '@/utils';

// 定义组件属性
const props = defineProps({
  /** 比赛类型 */
  comp: {
    type: String,
    required: true,
    validator: (value: string) => {
      return value.length > 0;
    },
  },
});

const $message = inject<(msg: string, type?: 'success' | 'error' | 'warning') => void>('$message');
const listRef = ref<any>(null);
const raceId = ref<string>('');
const raceList = ref<any[]>([]);
const downloadingFiles = ref<Set<string>>(new Set());
const currentListTotal = ref(0);

const getCurrentRaceId = () => {
  const searchFormData = listRef.value?.searchFormData;
  const currentFormData = searchFormData?.value ?? searchFormData ?? {};
  return currentFormData.raceId ?? raceId.value ?? '';
};

const raceSelect = computed(() => ({
  prop: 'raceId',
  label: '比赛届数',
  labelWidth: 100,
  formType: 'select',
  placeholder: '请选择比赛届数',
  style: { width: '240px' },
  options: raceList.value.map(item => ({
    label: item?.raceName || String(item?.raceId || ''),
    value: String(item.raceId),
  })),
  clearable: false,
  filterable: true,
  rules: [{ required: true, message: '请选择比赛届数' }],
  onChange: (val: any) => {
    raceId.value = String(val || '');
  },
}));

const columns = computed(() => {
  const list: any[] = [
    {
      prop: 'raceId',
      label: '比赛届数',
      width: 1,
      hide: true,
      search: raceSelect.value,
    },
    {
      search: {
        prop: 'searchKey',
        label: '姓名',
        labelWidth: 100,
        formType: 'input',
      },
      width: 1,
    },

    {
      prop: 'fileName',
      label: '文件名全称',
      align: 'center',
    },
  ];
  list.push({
    type: 'operation',
    label: '操作',
    fixed: 'right',
    width: 140,
    align: 'center',
    render(data: any) {
      return (
        <div>
          <el-button
            type="primary"
            onClick={() => handleDownloadSingle(data.row)}
            loading={downloadingFiles.value.has(data.row.fileName)}
          >
            下载
          </el-button>
        </div>
      );
    },
  });
  return list;
});

// 获取raceid的函数
const getRaceId = async () => {
  try {
    // 如果已经有raceid，直接返回
    if (raceId.value) {
      return raceId.value;
    }

    const res = await cffeApi.querySupportRace(props.comp);
    const rawList = Array.isArray(res) ? res : [];
    const matched = rawList.filter(item => item?.raceFlag === props.comp);
    raceList.value = matched.length > 0 ? matched : rawList;

    const foundRaceId = raceList.value[0]?.raceId ? String(raceList.value[0].raceId) : '';
    if (foundRaceId) {
      raceId.value = foundRaceId;
    }
    return foundRaceId;
  } catch (error) {
    console.log('获取raceid失败:', error);
    return '';
  }
};

const initRaceSelect = async () => {
  const currentRaceId = await getRaceId();
  if (!currentRaceId) return;
  const searchFormData = listRef.value?.searchFormData;
  const currentFormData = searchFormData?.value ?? searchFormData ?? {};
  if (currentFormData.raceId !== currentRaceId) {
    listRef.value?.setSearchForm({ ...currentFormData, raceId: currentRaceId });
  }
  listRef.value?.doSearch();
};

// 页面加载时预获取raceid
onMounted(() => {
  initRaceSelect();
});

// 监听comp变化，重新获取raceid
watch(
  () => props.comp,
  () => {
    raceId.value = '';
    raceList.value = [];
    initRaceSelect();
  }
);

watch(
  () => raceId.value,
  value => {
    if (!value) return;
    const searchFormData = listRef.value?.searchFormData;
    const currentFormData = searchFormData?.value ?? searchFormData ?? {};
    if (currentFormData.raceId !== value) {
      listRef.value?.setSearchForm({ ...currentFormData, raceId: value });
    }
  }
);

const fetch = async (params: any) => {
  const currentRaceId = params.raceId ?? getCurrentRaceId();
  if (!currentRaceId) {
    currentListTotal.value = 0;
    return { list: [], total: 0 };
  }

  raceId.value = String(currentRaceId);
  params.raceId = String(currentRaceId);
  params.comp = props.comp;
  const res = await cffeApi.getCertDigitalList(params);
  currentListTotal.value = res.total || 0;
  return { list: res.records || [], total: res.total || 0 };
};

const { loading: btnLoading, fn: downloadRequest } = useAsyncLoading(raceId => {
  return cffeApi.downloadCertDigitalList({ raceId });
});

// 批量下载全部证书
const handleDownloadCertDigital = async () => {
  const currentRaceId = raceId.value;
  if (!currentRaceId) {
    return;
  }
  if (currentListTotal.value <= 0) {
    $message?.('当前没有可下载项', 'warning');
    return;
  }

  try {
    const res = await downloadRequest(currentRaceId);
    download.zip(res, '证书.zip');
  } catch (error) {
    console.log('下载失败:', error);
  }
};

// 下载单个证书
const handleDownloadSingle = async (row: any) => {
  // 确保获取到raceid后再发送请求
  const currentRaceId = await getRaceId();
  if (!currentRaceId) {
    return;
  }
  const { fileName } = row;

  // 添加到下载中集合
  downloadingFiles.value.add(fileName);

  try {
    const res = await cffeApi.downloadSingle({ raceId: currentRaceId, fileName });
    download.downloadFile(res, fileName, 'application/octet-stream');
  } catch (error) {
    console.log('下载失败:', error);
  } finally {
    // 从下载中集合移除
    downloadingFiles.value.delete(fileName);
  }
};
</script>

<style scoped lang="scss">
.page {
  height: 100%;
  padding: 20px;
  .list-page {
    height: 100%;
  }
}
</style>
